<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddProduct from './AddProduct.vue';
import EditProduct from './EditProduct.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedProduct = ref({});
const loading = ref({});

const accountId = computed(() => store.getters.getCurrentAccountId);
const records = computed(() => getters['products/getProducts'].value);
const uiFlags = computed(() => getters['products/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedProduct.value.name}?`);

const fetchList = () => store.dispatch('products/get', { accountId: accountId.value });

const openAddPopup = () => { showAddPopup.value = true; };
const hideAddPopup = () => { showAddPopup.value = false; };

const openEditPopup = product => {
  selectedProduct.value = product;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
  selectedProduct.value = {};
};

const openDeletePopup = product => {
  selectedProduct.value = product;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => { showDeleteConfirmationPopup.value = false; };

const confirmDeletion = async () => {
  const id = selectedProduct.value.id;
  closeDeletePopup();
  loading.value[id] = true;
  try {
    await store.dispatch('products/delete', { accountId: accountId.value, id });
    useAlert(t('PRODUCTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || t('PRODUCTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const tableHeaders = computed(() => [
  t('PRODUCTS.LIST.TABLE_HEADER.NAME'),
  t('PRODUCTS.LIST.TABLE_HEADER.KIND'),
  t('PRODUCTS.LIST.TABLE_HEADER.STATUS'),
  t('PRODUCTS.LIST.TABLE_HEADER.PRICE_MIN'),
  t('PRODUCTS.LIST.TABLE_HEADER.VARIANTS'),
]);

onBeforeMount(() => { fetchList(); });
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('PRODUCTS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('PRODUCTS.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PRODUCTS.HEADER')"
        :description="$t('PRODUCTS.DESCRIPTION')"
      >
        <template #actions>
          <Button icon="i-lucide-circle-plus" :label="$t('PRODUCTS.HEADER_BTN_TXT')" @click="openAddPopup" />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="product in records" :key="product.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">{{ product.name }}</span>
              <div v-if="product.description" class="text-xs text-n-slate-10 mt-1 line-clamp-2">
                {{ product.description }}
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ $t(`PRODUCTS.KIND.${product.kind}`) }}
              <span v-if="product.kind === 'service' && product.duration_minutes" class="text-xs text-n-slate-10 ml-1">
                ({{ product.duration_minutes }}min)
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="{
                  'bg-green-100 text-green-700': product.status === 'active',
                  'bg-yellow-100 text-yellow-700': product.status === 'draft',
                  'bg-slate-100 text-slate-600': product.status === 'archived' || product.status === 'unlisted',
                }"
              >
                {{ $t(`PRODUCTS.STATUS.${product.status}`) }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ product.price_min != null ? `R$ ${Number(product.price_min).toFixed(2)}` : '-' }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ (product.variants || []).length }}
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button v-tooltip.top="$t('PRODUCTS.FORM.EDIT')" icon="i-lucide-pen" slate xs faded :is-loading="loading[product.id]" @click="openEditPopup(product)" />
                <Button v-tooltip.top="$t('PRODUCTS.FORM.DELETE')" icon="i-lucide-trash-2" xs ruby faded :is-loading="loading[product.id]" @click="openDeletePopup(product)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddProduct @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditProduct :selected-product="selectedProduct" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('PRODUCTS.DELETE.CONFIRM.TITLE')"
      :message="$t('PRODUCTS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('PRODUCTS.DELETE.CONFIRM.YES')"
      :reject-text="$t('PRODUCTS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
